// Utilidades de la disponibilidad semanal de un servicio.
// Forma de los datos: [{ day: 'Lunes', slots: [{ inicio: '09:00', fin: '18:00' }] }]

export const DIAS_SEMANA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

// Función (no constante) para que cada día reciba su propio objeto
export const horarioVacio = () => ({ inicio: '', fin: '' });

const pad = (n) => n.toString().padStart(2, '0');

// 'HH:MM' -> minutos del día
export const aMinutos = (hhmm) => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

// Un horario es inválido si la hora de fin no es posterior a la de inicio.
// Excepción: 00:00 en "fin" se interpreta como medianoche (fin del día), el máximo permitido.
export const slotInvalido = (slot) => {
  if (!slot.inicio || !slot.fin) return false;
  const ini = aMinutos(slot.inicio);
  const fin = slot.fin === '00:00' ? 1440 : aMinutos(slot.fin);
  return fin <= ini;
};

// Etiqueta amigable 12h: '00:00' -> '12:00 am', '13:30' -> '1:30 pm'
export const etiquetaHora = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  const periodo = h < 12 ? 'am' : 'pm';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad(m)} ${periodo}`;
};

// Opciones por hora en punto. Para 'fin' se omite 00:00 al inicio y se agrega
// "12:00 am (medianoche)" al final como tope del día.
export const opcionesHora = (tipo) => {
  const arr = [];
  for (let h = tipo === 'fin' ? 1 : 0; h < 24; h++) {
    const v = `${pad(h)}:00`;
    arr.push({ value: v, label: etiquetaHora(v) });
  }
  if (tipo === 'fin') arr.push({ value: '00:00', label: '12:00 am (medianoche)' });
  return arr;
};

// Los días siempre se guardan en orden de la semana (Lunes primero)
export const ordenarDias = (disponibilidad = []) =>
  [...disponibilidad].sort((a, b) => DIAS_SEMANA.indexOf(a.day) - DIAS_SEMANA.indexOf(b.day));

const firma = (dia) => JSON.stringify((dia.slots || []).map(s => [s.inicio, s.fin]));

// ¿Todos los días tienen exactamente los mismos horarios?
export const todosIguales = (disponibilidad = []) =>
  disponibilidad.every(d => firma(d) === firma(disponibilidad[0]));

// "9:00 am – 6:00 pm"; con turno partido "9:00 am – 2:00 pm y 4:00 pm – 8:00 pm".
// Solo cuenta los horarios completos y válidos; si no hay ninguno devuelve ''.
export const resumenHorarios = (slots = []) =>
  slots
    .filter(s => s.inicio && s.fin && !slotInvalido(s))
    .map(s => `${etiquetaHora(s.inicio)} – ${etiquetaHora(s.fin)}`)
    .join(' y ');

// ['Lunes', ..., 'Viernes'] -> "Lunes a viernes"; los 7 -> "Todos los días";
// ['Sábado', 'Domingo'] -> "Sábado y domingo"; ['Lunes', 'Miércoles'] -> "Lunes y miércoles"
export const resumenDias = (dias = []) => {
  const indices = DIAS_SEMANA.map((d, i) => (dias.includes(d) ? i : -1)).filter(i => i >= 0);
  if (indices.length === 0) return '';
  if (indices.length === DIAS_SEMANA.length) return 'Todos los días';

  // Rachas de días consecutivos
  const rachas = [];
  indices.forEach(i => {
    const ultima = rachas[rachas.length - 1];
    if (ultima && ultima[ultima.length - 1] === i - 1) ultima.push(i);
    else rachas.push([i]);
  });
  // Una racha de 3 o más se abrevia ("Lunes a viernes"); las demás se nombran una por una
  const partes = rachas.flatMap(r => (r.length >= 3
    ? [`${DIAS_SEMANA[r[0]]} a ${DIAS_SEMANA[r[r.length - 1]].toLowerCase()}`]
    : r.map(i => DIAS_SEMANA[i])));

  const texto = partes.map((p, i) => (i === 0 ? p : p.toLowerCase()));
  if (texto.length === 1) return texto[0];
  return `${texto.slice(0, -1).join(', ')} y ${texto[texto.length - 1]}`;
};
